import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, CircularProgress, Paper } from "@mui/material";

import type { DealCompsAnalysis } from "./DealCompSectionTypes";

/** All text in this module uses this stack (icons keep MUI’s icon font). */
const HELVETICA_NEUE = '"Helvetica Neue", Helvetica, Arial, sans-serif';

/** Matches comps average / this deal value line (20px × 1.2 line-height). */
const METRIC_VALUE_HEIGHT_PX = 24;

export interface CompAnalysisProps {
  analysis: DealCompsAnalysis | null;
  /** When true, comps average values show a spinner (e.g. filter save + comps refresh). */
  compsAverageLoading?: boolean;
}

function fmtPlain(n: number | null, fractionDigits = 2): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return n.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

function fmtPct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return `${n.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

type MetricTheme =
  | "lease"
  | "sales"
  | "salesPricePerArea"
  | "loanMargin"
  | "loanLtv";

const themes: Record<
  MetricTheme,
  {
    headerBg: string;
    headerBgDark: string;
    headerText: string;
    badgeBg: string;
    badgeBgDark: string;
    badgeText: string;
    badgeTextDark: string;
    shadow: string;
  }
> = {
  lease: {
    headerBg: "#E8F5E9",
    headerBgDark: "rgba(129, 199, 132, 0.22)",
    headerText: "#1B5E20",
    badgeBg: "#C8E6C9",
    badgeBgDark: "rgba(102, 187, 106, 0.35)",
    badgeText: "#2E7D32",
    badgeTextDark: "#A5D6A7",
    shadow: "rgba(46, 125, 50, 0.2)",
  },
  sales: {
    headerBg: "#E3F2FD",
    headerBgDark: "rgba(100, 181, 246, 0.2)",
    headerText: "#0D47A1",
    badgeBg: "#BBDEFB",
    badgeBgDark: "rgba(66, 165, 245, 0.35)",
    badgeText: "#1565C0",
    badgeTextDark: "#90CAF9",
    shadow: "rgba(21, 101, 192, 0.2)",
  },
  salesPricePerArea: {
    headerBg: "#E0F2F1",
    headerBgDark: "rgba(0, 150, 136, 0.2)",
    headerText: "#004D40",
    badgeBg: "#B2DFDB",
    badgeBgDark: "rgba(0, 172, 193, 0.32)",
    badgeText: "#00695C",
    badgeTextDark: "#80CBC4",
    shadow: "rgba(0, 105, 92, 0.22)",
  },
  loanMargin: {
    headerBg: "#FCE4EC",
    headerBgDark: "rgba(244, 143, 177, 0.18)",
    headerText: "#880E4F",
    badgeBg: "#F8BBD0",
    badgeBgDark: "rgba(240, 98, 146, 0.32)",
    badgeText: "#AD1457",
    badgeTextDark: "#F48FB1",
    shadow: "rgba(173, 20, 87, 0.18)",
  },
  loanLtv: {
    headerBg: "#FFF3E0",
    headerBgDark: "rgba(255, 183, 77, 0.2)",
    headerText: "#E65100",
    badgeBg: "#FFE0B2",
    badgeBgDark: "rgba(255, 167, 38, 0.35)",
    badgeText: "#E65100",
    badgeTextDark: "#FFCC80",
    shadow: "rgba(230, 81, 0, 0.22)",
  },
};

function diffBadgeText(n: number | null): string | null {
  if (n === null || !Number.isFinite(n)) return null;
  const suffix = "%";
  if (n === 0) return `0${suffix}`;
  const abs = Math.abs(n).toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${n < 0 ? "-" : "+"}${abs}${suffix}`;
}

function AnalysisMetricCard({
  title,
  theme,
  comp,
  deal,
  diff,
  format,
  compsAverageLoading = false,
}: {
  title: string;
  theme: MetricTheme;
  comp: number | null;
  deal: number | null;
  diff: number | null;
  format: "plain" | "pct";
  compsAverageLoading?: boolean;
}) {
  const t = themes[theme];
  const fmt = format === "pct" ? fmtPct : fmtPlain;
  const diffText = diffBadgeText(diff);
  const hasDiffBadge = diffText !== null;
  const showBadge = hasDiffBadge && !compsAverageLoading;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 160px",
        minWidth: 160,
        maxWidth: "100%",
        boxSizing: "border-box",
        borderRadius: "12px",
        overflow: "visible",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        fontFamily: HELVETICA_NEUE,
        boxShadow: (muiTheme) =>
          muiTheme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.35)"
            : "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
      }}
    >
      <Box
        sx={{
          borderRadius: "12px 12px 0 0",
          overflow: "hidden",
          bgcolor: (m) =>
            m.palette.mode === "dark" ? t.headerBgDark : t.headerBg,
          px: 2,
          py: 1.25,
        }}
      >
        <Box
          component="div"
          role="heading"
          aria-level={3}
          sx={{
            m: 0,
            fontWeight: 600,
            fontSize: "0.875rem",
            letterSpacing: "0.01em",
            lineHeight: 1.3,
            color: (m) =>
              m.palette.mode === "dark" ? t.badgeTextDark : t.headerText,
            fontFamily: HELVETICA_NEUE,
          }}
        >
          {title}
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          alignItems: "stretch",
          minHeight: hasDiffBadge ? 72 : 54,
          pb: hasDiffBadge ? 2.75 : 2,
          pt: 2,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            borderRight: "1px solid",
            borderColor: "divider",
            px: 1,
          }}
        >
          <Box
            component="div"
            sx={{
              m: 0,
              display: "block",
              color: "text.secondary",
              fontWeight: 500,
              fontSize: "0.7rem",
              lineHeight: 1.2,
              textTransform: "none",
              mb: 0.75,
              fontFamily: HELVETICA_NEUE,
            }}
          >
            Comps average
          </Box>
          <Box
            component="div"
            aria-busy={compsAverageLoading}
            sx={{
              m: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: METRIC_VALUE_HEIGHT_PX,
              minHeight: METRIC_VALUE_HEIGHT_PX,
              fontSize: "20px",
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              lineHeight: `${METRIC_VALUE_HEIGHT_PX}px`,
              color: "text.primary",
              fontFamily: HELVETICA_NEUE,
              "& .MuiCircularProgress-root": {
                width: METRIC_VALUE_HEIGHT_PX,
                height: METRIC_VALUE_HEIGHT_PX,
              },
            }}
          >
            {compsAverageLoading ? (
              <CircularProgress
                size={METRIC_VALUE_HEIGHT_PX}
                thickness={4}
                aria-label="Loading"
              />
            ) : (
              fmt(comp)
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 1,
          }}
        >
          <Box
            component="div"
            sx={{
              m: 0,
              display: "block",
              color: "text.secondary",
              fontWeight: 500,
              fontSize: "0.7rem",
              lineHeight: 1.2,
              textTransform: "none",
              mb: 0.75,
              fontFamily: HELVETICA_NEUE,
            }}
          >
            This deal
          </Box>
          <Box
            component="div"
            sx={{
              m: 0,
              display: "block",
              height: METRIC_VALUE_HEIGHT_PX,
              fontSize: "20px",
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              lineHeight: `${METRIC_VALUE_HEIGHT_PX}px`,
              color: "text.primary",
              fontFamily: HELVETICA_NEUE,
            }}
          >
            {fmt(deal)}
          </Box>
        </Box>

        {showBadge && diff !== null && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: 6,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              px: 1.25,
              py: 0.35,
              borderRadius: "999px",
              bgcolor: (m) =>
                m.palette.mode === "dark" ? t.badgeBgDark : t.badgeBg,
              boxShadow: `0 2px 8px ${t.shadow}`,
              border: "1px solid",
              borderColor: (m) =>
                m.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(255,255,255,0.9)",
            }}
          >
            {diff > 0 ? (
              <ArrowUpwardIcon
                sx={{
                  fontSize: 14,
                  color: (m) =>
                    m.palette.mode === "dark" ? t.badgeTextDark : t.badgeText,
                }}
              />
            ) : diff < 0 ? (
              <ArrowDownwardIcon
                sx={{
                  fontSize: 14,
                  color: (m) =>
                    m.palette.mode === "dark" ? t.badgeTextDark : t.badgeText,
                }}
              />
            ) : null}
            <Box
              component="div"
              sx={{
                m: 0,
                display: "block",
                fontWeight: 700,
                fontSize: "0.75rem",
                lineHeight: 1.2,
                fontVariantNumeric: "tabular-nums",
                color: (m) =>
                  m.palette.mode === "dark" ? t.badgeTextDark : t.badgeText,
                fontFamily: HELVETICA_NEUE,
              }}
            >
              {diffText}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export function CompAnalysis({
  analysis,
  compsAverageLoading = false,
}: CompAnalysisProps) {
  if (!analysis) return null;

  const a = analysis;
  const cardProps = { compsAverageLoading };

  return (
    <Box
      component="section"
      aria-label="Comps analysis overview"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: 2,
        overflow: "visible",
        py: 0.25,
        pb: 0.5,
        boxSizing: "border-box",
        fontFamily: HELVETICA_NEUE,
      }}
    >
      <AnalysisMetricCard
        title="Leases — Rent / Area"
        theme="lease"
        comp={a.comps_avg_leases_rent_per_area}
        deal={a.deal_leases_rent_per_area}
        diff={a.difference_leases_rent_per_area}
        format="plain"
        {...cardProps}
      />
      <AnalysisMetricCard
        title="Sales — Yield"
        theme="sales"
        comp={a.comps_avg_sales_yield_pct}
        deal={a.deal_sales_yield_pct}
        diff={a.difference_sales_yield_pct}
        format="pct"
        {...cardProps}
      />
      <AnalysisMetricCard
        title="Sales — Price / Area"
        theme="salesPricePerArea"
        comp={a.comps_avg_price_per_area}
        deal={a.deal_price_per_area}
        diff={a.difference_price_per_area_pct}
        format="plain"
        {...cardProps}
      />
      <AnalysisMetricCard
        title="Loans — Margin"
        theme="loanMargin"
        comp={a.comps_avg_loans_margin}
        deal={a.deal_loans_margin}
        diff={a.difference_loans_margin}
        format="pct"
        {...cardProps}
      />
      <AnalysisMetricCard
        title="Loans — LTV"
        theme="loanLtv"
        comp={a.comps_avg_loans_ltv}
        deal={a.deal_loans_ltv}
        diff={a.difference_loans_ltv}
        format="pct"
        {...cardProps}
      />
    </Box>
  );
}

export default CompAnalysis;
